import React, { useCallback, useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";

import { AppDispatch } from "src/redux/store";
import TableLoadingShell from "src/utils/TableLoadingShell";
import {
  fetchHistoryLogsAsync,
  getChangedByUserListAsync,
  getModuleListAsync,
} from "src/redux/actions/historyLogsActions";
import { fetchHistoryLogPlatformsAsync } from "src/redux/actions/platformActions";
import AppDatePickerRange from "../orders/components/AppDatePickerRange";
import { useAppSelector } from "src/redux/storeHooks";
import { extractFilterValue } from "src/marketConnect/helpers";
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import StatusButton from "../orders/StatusButton";
import FilterDropdown from "src/marketConnect/helpers/components/FilterDropdown";

interface HistoryLogsParams {
  user_id?: number;
  platform_id?: number;
  content_type_id?: number;
  category?: string;
  changed_by?: number;
  changed_at_max?: string;
  changed_at_min?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  search?: string;
}

// Chevron component for See More / See Less
const Chevron = ({ open }: { open: boolean }) => (
  <span
    style={{
      display: "inline-block",
      transition: "0.2s",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    ▼
  </span>
);

const NewHistoryLogs: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  /* -------------------- REDUX STATE -------------------- */
  const { historyLogsData, historyLogsLoading, pagination, moduleList, columnsList, changedByList } =
    useSelector((state: any) => state.newhistoryLogsSlice);
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData);
  const userId = fetchedUserAccountData?.id;

  /* -------------------- LOCAL STATE -------------------- */
  const [channelPlatforms, setChannelPlatforms] = useState<{ id: number; display_name: string }[]>([]);
  const [queryParams, setQueryParams] = useState<HistoryLogsParams>({
    page: 1,
    page_size: 10,
    user_id: Number(userId),
  });
  const [selectedRange, setSelectedRange] = useState<any>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [id: number]: boolean }>({});
  const [searchString, setSearchString] = useState("");
  const [filters, setFilters] = useState<any>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const platformKey = "platform_name";
  const moduleKey = "section";
  const changedBy = "changed_by_email";

  const allCategories = moduleList?.map((m: any) => m.category) || [];
  const uniqueCategories = Array.from(new Set(allCategories));

  useEffect(() => {
    dispatch(fetchHistoryLogPlatformsAsync())
      .unwrap()
      .then((response) => setChannelPlatforms(response?.data || []))
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to fetch platforms");
      });
  }, [dispatch]);

  /* -------------------- FILTER OPTIONS -------------------- */
  // PLATFORMS (CHANNELS)
  const platformFilterOptions: string[] = channelPlatforms.map((platform) => platform.display_name);

  // MODULES
  const moduleFilterOptions: string[] = uniqueCategories.map((cat) => String(cat)) || [];

  // CHANGED BY USERS
  const changedByFilterOptions: string[] = changedByList?.map((user: any) => user.email) || [];

  // Map display name / email -> API key/id
  const platformMap: Record<string, number> = {};

  channelPlatforms.forEach((platform) => { platformMap[platform.display_name] = platform.id; });

  const moduleMap: Record<string, string> = {};
  moduleList?.forEach((m: any) => {
    moduleMap[m.category] = m.category; // API expects category string
  });

  const changedByMap: Record<string, number> = {}; changedByList?.forEach((u: any) => {
    changedByMap[u.email] = u.id;
  });

  /* -------------------- FETCH LOGS -------------------- */
  const fetchLogs = useCallback(() => {
    dispatch(fetchHistoryLogsAsync(queryParams))
      .unwrap()
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to fetch history logs");
      });
  }, [queryParams]);

  useEffect(() => {
    const getValue = (val: any) => typeof val === "object" ? val?.label : val;
    const platformName = getValue(filters[platformKey]);
    const moduleName = getValue(filters[moduleKey]);
    const changedByEmail = getValue(filters[changedBy]);

    setQueryParams((prev) => ({
      ...prev,
      platform_id: platformName ? platformMap[platformName] : undefined,
      category: moduleName ? moduleMap[moduleName] : undefined,
      changed_by: changedByEmail ? changedByMap[changedByEmail] : undefined,
      page: 1,
    }));
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    dispatch(getModuleListAsync({}));
    dispatch(getChangedByUserListAsync({ user_id: Number(userId) }));
  }, [dispatch, userId]);

  /* -------------------- RESET FILTERS -------------------- */
  const handleResetFilters = () => {
    setFilters({});
    setSelectedRange(null);
    setSearchString("");
    setQueryParams({
      page: 1,
      page_size: 10,
      user_id: Number(userId),
    });
  };

  /* -------------------- FILTERED TABLE DATA -------------------- */
  const filteredData = historyLogsData.filter((row) => {
    if (row.diff_summary === null || row.diff_summary === undefined) return false;
    return true;
  });

  useEffect(() => {
    const platformName = extractFilterValue(filters[platformKey]);
    const moduleName = extractFilterValue(filters[moduleKey]);
    const changedByEmail = extractFilterValue(filters[changedBy]);

    setQueryParams((prev) => ({
      ...prev,
      platform_id: platformName ? platformMap[platformName] : undefined,
      category: moduleName ? moduleMap[moduleName] : undefined,
      changed_by: changedByEmail ? changedByMap[changedByEmail] : undefined,
      page: 1,
    }));
  }, [filters]);

const mapActionTypeToSyncStatus = (actionType: string) => {
  switch (actionType) {
    case "insert":
      return "INSERT";
    case "update":
      return "UPDATE";
    case "delete":
      return "DELETE";
    case "order_resync":
    case "product_resync":
    case "refresh_mapping":
      return actionType.toUpperCase(); // Returns 'PRODUCT_RESYNC', 'ORDER_RESYNC', etc.
    default:
      return "OTHER"; // Everything else
  }
};

  /* -------------------- TABLE COLUMNS WITH FILTERS -------------------- */
  const columns: TableColumn<any>[] = Object.keys(columnsList).map((key) => {
    // CHANNEL COLUMN
    if (key === platformKey) {
      return {
        name: (
          <>
            <span>Channel</span>
            <FilterDropdown
              id={`filter-${platformKey}`}
              filters={filters}
              setFilters={(newFilters: any) => {
                setFilters(newFilters);
              }}
              openFilterKey={openFilterKey}
              setOpenFilterKey={setOpenFilterKey}
              hasActiveFilter={!!filters[platformKey]}
              options={[{ label: "Filter Channel", key: platformKey, values: platformFilterOptions }]}
            />
          </>
        ),
        selector: (row) => row.platform_name || "-",
        center: true,
        minWidth: "180px",
      };
    }

    // ACTION TYPE COLUMN → STATUS BUTTON
    if (key === "action_type") {
      return {
        name: "Activity",
        center: true,
        width: "180px",
        cell: (row) => (
          <StatusButton
            syncStatus={mapActionTypeToSyncStatus(row.action_type)}
            syncMessage={row.short_title || ""}
          />
        ),
        columnKey: "action_type",
      };
    }

    // MODULE COLUMN
    if (key === moduleKey) {
      return {
        name: (
          <>
            <span>Section</span>
            <FilterDropdown
              id={`filter-${moduleKey}`}
              filters={filters}
              setFilters={(newFilters: any) => {
                setFilters(newFilters);

              }}
              openFilterKey={openFilterKey}
              setOpenFilterKey={setOpenFilterKey}
              hasActiveFilter={!!filters[moduleKey]}
              options={[{ label: "Filter Module", key: moduleKey, values: moduleFilterOptions }]}
            />
          </>
        ),
        selector: (row) => row.section || "-",
        center: true,
        minWidth: "180px",
      };
    }

    // CHANGED BY COLUMN
    if (key === changedBy) {
      return {
        name: (
          <>
            <span>Changed By</span>
            <FilterDropdown
              id={`filter-${changedBy}`}
              filters={filters}
              setFilters={(newFilters: any) => {
                setFilters(newFilters);

              }}
              openFilterKey={openFilterKey}
              setOpenFilterKey={setOpenFilterKey}
              hasActiveFilter={!!filters[changedBy]}
              options={[{ label: "Filter User", key: changedBy, values: changedByFilterOptions }]}
            />
          </>
        ),
        selector: (row) => row.changed_by_email || "-",
        center: true,
        minWidth: "200px",
      };
    }

    // DATE & TIME COLUMN (changed_at)
    if (key === "changed_at") {
      return {
        name: (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>Date & Time</span>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
            >
              <span style={{ cursor: "pointer", color: "darkgray" }}>
                <i className="fa fa-info-circle" />
              </span>
            </OverlayTrigger>
          </div>
        ),
        center: true,
        width: "180px",
        sortable: true,
        selector: (row) =>
          row?.changed_at ? formatDateLocal(row.changed_at) : "-",
        columnKey: "changed_at",
      };
    }

    // DEFAULT COLUMN
    return {
      name: columnsList[key],
      center: true,
      selector: (row) => {
        const value = row[key];
        if (!value) return "-";


        if (key === "diff_summary" && typeof value === "object") {
          const message = value.Message || "-";
          const isExpanded = expandedRows[row.id] || false;
          const maxChars = 100;
          const displayText = isExpanded ? message : message.substring(0, maxChars);

          return (
            <div>
              {displayText.split("\n").map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
              {message.length > maxChars && (
                <button
                  className="btn btn-link btn-sm p-0 mt-1 d-flex align-items-center gap-1"
                  onClick={() =>
                    setExpandedRows((prev) => ({ ...prev, [row.id]: !isExpanded }))
                  }
                >
                  {isExpanded ? "See Less" : "See More"}
                  <Chevron open={isExpanded} />
                </button>
              )}
            </div>
          );
        }

        if (typeof value === "object") return JSON.stringify(value);
        return value;
      },
      minWidth: "160px",
      grow: key === "short_title" ? 2 : 1,
      wrap: true,
      width: key === "diff_summary" ? "250px" : undefined,
      sortable: key === "changed_at",
      columnKey: key,
    };
  });

  /* -------------------- RENDER -------------------- */
  return (
     <div className='history-logs shadow rounded border-0 p-4'>
      {/* <div className="mb-3 d-flex justify-content-between align-items-center">
        <span className="fw-bold text-lg" style={{ fontSize: "16px" }}>
          Application Logs
        </span>
      </div> */}

      <div className="card card-flush">
        {/* HEADER / SEARCH + DATE PICKER + RESET */}
        <div
          className="card-header history-logs__toolbar"
        >
          {/* SEARCH */}
          <div className="position-relative history-logs__search">
            {searchString === "" && (
              <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"></i>
            )}
            <input
              type="search"
              className="form-control"
              placeholder="Search"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setQueryParams((prev) => ({ ...prev, search: searchString, page: 1 }));
                }
              }}
            />
          </div>

          {/* DATE PICKER + RESET */}
          <div className="d-flex align-items-center gap-2 history-logs__actions">
            <button
              className="btn btn-outline-primary btn-sm date-btn"
              onClick={() => setIsDatePickerVisible(true)}
            >
              <FontAwesomeIcon icon={faCalendar} className="me-2" />
              {selectedRange
                ? `${selectedRange.startDate.split("T")[0]} → ${selectedRange.endDate.split("T")[0]}`
                : "Pick Date Range"}
            </button>

            <AppDatePickerRange
              isVisible={isDatePickerVisible}
              handleClose={() => setIsDatePickerVisible(false)}
              onPickRange={({ startDate, endDate }) => {
                setSelectedRange({ startDate, endDate });
                setQueryParams((prev) => ({
                  ...prev,
                  changed_at_min: startDate.split("T")[0],
                  changed_at_max: endDate.split("T")[0],
                  page: 1,
                }));
              }}
            />

            <button className="btn btn-sm btn-primary fw-bold px-4" onClick={handleResetFilters}>
              Reset Filters
            </button>
          </div>
        </div>

        {/* TABLE */}
        <TableLoadingShell loading={historyLogsLoading}>
        <DataTable
          fixedHeader
          persistTableHead
          data={filteredData}
          columns={columns}
          pagination
          paginationServer
          paginationTotalRows={pagination.count}
          paginationPerPage={queryParams.page_size}
          onChangePage={(page) => setQueryParams((prev) => ({ ...prev, page }))}
          onChangeRowsPerPage={(page_size) =>
            setQueryParams((prev) => ({ ...prev, page_size, page: 1 }))
          }
          progressPending={false}
          noDataComponent={<div>No records found</div>}
          sortServer
          onSort={(column, sortDirection) => {
            if ((column as any).columnKey === "changed_at") {
              setQueryParams((prev) => ({
                ...prev,
                changed_at: sortDirection,
                page: 1,
              }));
            }
          }}
          customStyles={{
            table: { style: { height: "55vh" } },
            headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
            cells: { style: { padding: "10px", borderBottom: "1px solid gainsboro" } },
          }}
        />
        </TableLoadingShell>
      </div>
    </div>
  );
};

export default NewHistoryLogs;
